<template>
  <div>

    <b-row>
      <b-col md="8">
        <h4 class="font-weight-bold ml-1">Kunderapport</h4>
      </b-col>

      <b-col md="4">
        <div class="float-right">
          <label class="nav-item navbar-text navbar-search-box p-0 active">
            <i class="ion ion-ios-search navbar-icon align-middle"></i>
            <span class="navbar-search-input pl-2">
              <b-form-input
                class="ml-2"
                v-model="filter"
                placeholder="Søk"
              ></b-form-input>
            </span>
          </label>
        </div>
      </b-col>
    </b-row>

    <div
      v-if="isLoading"
      class="text-center my-5"
    >
      <b-spinner variant="primary"></b-spinner>
    </div>

    <div v-else>
      <b-table
        show-empty
        caption-top
        stacked="sm"
        :items="customerTotals"
        :fields="fields"
        :current-page="currentPage"
        :per-page="perPage"
        :total-rows="totalRows"
        :filter="filter"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :sort-direction="sortDirection"
        :hover="hoverTable"
        empty-text="Ingen ordre å vise"
      >
        <template v-slot:table-caption>
          <b-row>
            <b-col md="3">
              <span>Totalt: {{ customerTotals.length | numeralFormat }} kunder</span>
            </b-col>
            <b-col md="3">
              <span>{{ orders.length | numeralFormat }} ordre</span>
            </b-col>
          </b-row>
        </template>

        <template v-slot:cell(invoice_hours)="data">
          kr {{ data.value | numeralFormat("0,0[.]") }},-
        </template>

        <template v-slot:cell(invoice_biomass)="data">
          kr {{ data.value | numeralFormat("0,0[.]") }},-
        </template>

        <template v-slot:cell(invoice_total)="data">
          <strong>kr {{ data.value | numeralFormat("0,0[.]") }},-</strong>
        </template>

        <template v-slot:cell(actions)="row">
          <b-button
            size="sm"
            variant="primary"
            @click="row.toggleDetails"
          >{{ row.detailsShowing ? "Skjul" : "Vis" }} ordre</b-button>
        </template>

        <template v-slot:custom-foot>
          <tr>
            <td class="font-weight-bold">Totalt</td>
            <td class="font-weight-bold">{{ grandTotal.orderCount | numeralFormat }}</td>
            <td class="font-weight-bold">kr {{ grandTotal.invoice_hours | numeralFormat("0,0[.]") }},-</td>
            <td class="font-weight-bold">kr {{ grandTotal.invoice_biomass | numeralFormat("0,0[.]") }},-</td>
            <td class="font-weight-bold">kr {{ grandTotal.invoice_total | numeralFormat("0,0[.]") }},-</td>
            <td></td>
          </tr>
        </template>

        <template v-slot:row-details="row">
          <b-card>
            <b-table
              striped
              small
              :items="row.item.orders"
              :fields="orderDetailFields"
            >
              <template v-slot:cell(ordertitle)="data">
                <b-link
                  class="link"
                  :href="'#/dashboard/order-edit/' + data.item.id"
                >
                  {{ data.item.ordernumber }} - {{ data.item.type }}
                  <span v-if="data.item.site != null">({{ data.item.site.name }})</span>
                </b-link>
              </template>

              <template v-slot:cell(status)="data">
                <b-badge
                  pill
                  variant="secondary"
                >{{ data.value }}</b-badge>
              </template>

              <template v-slot:cell(invoice_hours)="data">
                kr {{ data.value | numeralFormat("0,0[.]") }},-
              </template>

              <template v-slot:cell(invoice_biomass)="data">
                kr {{ data.value | numeralFormat("0,0[.]") }},-
              </template>

              <template v-slot:cell(invoice_total)="data">
                kr {{ data.value | numeralFormat("0,0[.]") }},-
              </template>
            </b-table>
          </b-card>
        </template>
      </b-table>

      <b-row>
        <b-col
          md="1"
          class="my-1"
        >
          <b-form-group class="mb-0">
            <b-form-select
              v-model="perPage"
              :options="pageOptions"
            ></b-form-select>
          </b-form-group>
        </b-col>

        <b-col
          md="2"
          class="my-1"
        >
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            class="my-0"
          ></b-pagination>
        </b-col>
      </b-row>
    </div>

  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      orders: [],
      customerTotals: [],
      fields: [
        { key: "customer", label: "Kunde", sortable: true, class: "text-left" },
        { key: "orderCount", label: "Antall ordre", sortable: true },
        { key: "invoice_hours", label: "Arbeidstimer", sortable: true },
        { key: "invoice_biomass", label: "Tillegg", sortable: true },
        { key: "invoice_total", label: "Totalt", sortable: true },
        { key: "actions", label: "" },
      ],
      orderDetailFields: [
        { key: "ordertitle", label: "Ordre", class: "text-left" },
        { key: "status", label: "Status" },
        { key: "invoice_hours", label: "Arbeidstimer" },
        { key: "invoice_biomass", label: "Tillegg" },
        { key: "invoice_total", label: "Totalt" },
      ],
      totalRows: 1,
      currentPage: 1,
      perPage: 100,
      pageOptions: [25, 50, 100, 250, 500, 1000],
      sortBy: "invoice_total",
      sortDesc: true,
      sortDirection: "desc",
      filter: null,
      hoverTable: true,
      isLoading: false,
    };
  },
  computed: {
    grandTotal() {
      return this.customerTotals.reduce(
        (totals, group) => {
          totals.orderCount += group.orderCount;
          totals.invoice_hours += group.invoice_hours;
          totals.invoice_biomass += group.invoice_biomass;
          totals.invoice_total += group.invoice_total;
          return totals;
        },
        { orderCount: 0, invoice_hours: 0, invoice_biomass: 0, invoice_total: 0 }
      );
    },
  },
  methods: {
    async getOrders() {
      this.isLoading = true;

      try {
        const company = localStorage.getItem("company");
        const res = await axios.get(
          `https://api.eines-as.no/orders?_limit=-1&_sort=ordernumber:DESC&status_eq=Til fakturering&status_eq=Fakturert&_where[supplier_company]=${company}`
        );
        this.orders = res.data;
        this.buildCustomerTotals();
      } catch (err) {
        console.error("Failed to load orders:", err);
      } finally {
        this.isLoading = false;
      }
    },
    buildCustomerTotals() {
      var groups = {};
      var list = [];

      this.orders.forEach((order) => {
        var customerCompany = order.customer_company || {};
        var key = customerCompany.id || customerCompany.name || "ukjent";

        if (!groups[key]) {
          groups[key] = {
            customer: customerCompany.name || "Ukjent kunde",
            orders: [],
            _showDetails: false,
          };
          list.push(groups[key]);
        }

        groups[key].orders.push(order);
      });

      list.forEach((group) => {
        group.orderCount = group.orders.length;
        group.invoice_hours = group.orders.reduce(function (n, order) {
          return n + (order.invoice_hours || 0);
        }, 0);
        group.invoice_biomass = group.orders.reduce(function (n, order) {
          return n + (order.invoice_biomass || 0);
        }, 0);
        group.invoice_total = group.orders.reduce(function (n, order) {
          return n + (order.invoice_total || 0);
        }, 0);
      });

      list.sort(function (a, b) {
        return b.invoice_total - a.invoice_total;
      });

      this.customerTotals = list;
      this.totalRows = list.length;
    },
  },
  created: function () {
    this.getOrders();
  },
};
</script>

<style lang="css">
.link {
  color: #484848;
}

.link:link {
  color: #484848 !important;
}

.link:visited {
  color: #484848 !important;
}

.link:hover {
  color: #6e6e6e !important;
}

.link:active {
  color: #484848 !important;
}
</style>
